import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { Camera, Check, Plus, Printer, RefreshCw, ScanLine, Trash2 } from "lucide-react";
import React from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";

import { DateInput } from "@/components/DateInput";
import { PhotoCapture } from "@/components/PhotoCapture";
import { Button } from "@/components/ui/button";
import {
  acceptBatch,
  bindBarcode,
  deleteBatch,
  deleteDelivery,
  deleteDeliveryPhoto,
  fetchDeliveryPhotoUrl,
  finishDelivery,
  getDelivery,
  getReceivingConfig,
  getReceivingOrder,
  postDeliveryReceipt,
  printBatchLabels,
  refreshBatchSnapshot,
  reopenDelivery,
  retryInspectionTasks,
  scanCode,
  setDeliveryUpd,
  type BatchView,
  type OrderLineView,
  type PhotoMeta,
  type PlaceView,
  type ScanResponse,
  uploadDeliveryPhoto,
} from "@/api/receiving";
import { plural } from "@/lib/order-check";
import { batchLabel, partiaSuffix, updTitle } from "@/lib/receiving";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { LoadState, apiErrorText, useGoBack } from "@/lib/scan-ui/screen";
import { OkkBadge } from "@/pages/receiving/OkkBadge";
import { useScanCapture } from "@/lib/use-scan-capture";
import { useUploadProgress } from "@/lib/use-upload-progress";
import { type BlockedScan, BlockedScanBanner, ScanBanner } from "@/pages/fbs/ScanBanner";

// Приёмку ведут и с ТСД, и с телефона: ТСД стреляет в глобальный перехват без
// кнопки, а на телефоне сканера нет вовсе — без камеры код туда не ввести.

/** Приход (одна УПД): позиции заказа → карточка партии → наклейка и фото → финиш.
 *
 *  Ведёт ПОЗИЦИЯ, а не скан. Полноценный DataMatrix есть у одного поставщика из
 *  десятка, и строить экран на сканере значило бы сделать его неработающим в
 *  большинстве приёмок. Скан — ускоритель: узнал код → открыл карточку уже
 *  заполненной; не узнал → спросил, что это, и предложил запомнить.
 */

// Тон — из общего `ScanBanner`: правило «ошибка крупная и сама не гаснет»
// собрано на складе и должно соблюдаться во всех скан-экранах одинаково.
type Banner = { kind: "ok" | "warn" | "error"; text: string } | null;

interface BatchDraft {
  nomenclature_ref: string;
  nomenclature_name: string;
  /** Характеристика и единица СТРОКИ ЗАКАЗА. Без первой строка приходной
   *  получает нулевую характеристику (цена не находится и уходит нулём), без
   *  второй 1С приходует «9 коробов» как 9 штук. */
  characteristic_ref: string;
  characteristic_name: string;
  unit_ref: string;
  unit_type: string;
  unit_name: string;
  unit_coef: number;
  supplier_batch: string;
  /** Партия УЧЁТНАЯ: номер поставщика вводится руками, УПД не подставляем. */
  partia_tracked: boolean;
  production_date: string;
  expiry_date: string;
  qty: string;
  place_type: string;
  places: string;
  qty_per_place: string;
  /** Незнакомый код, который предложим запомнить за выбранной позицией. */
  pendingCode: string;
}

const PLACE_TYPES = ["канистра", "бочка", "мешок", "короб", "паллета", "ведро"];

/** Правка «мест» или «в месте» с пересчётом итога.
 *
 * Пересчитывает ОБА поля-сомножителя: раньше итог обновлялся только от «в
 * месте», и приёмка 12 канистр вместо одной уходила в 1С количеством одной —
 * молча, потому что итог выглядел заполненным. Пусто хотя бы в одном
 * сомножителе — итог не трогаем: человек мог ввести его руками (последнее
 * место бывает неполным), и затирать введённое нулём нельзя.
 */
function withTotal(draft: BatchDraft, patch: Partial<BatchDraft>): BatchDraft {
  const next = { ...draft, ...patch };
  const places = Number(next.places);
  const per = Number(next.qty_per_place);
  if (next.places !== "" && next.qty_per_place !== "" && !Number.isNaN(places) && !Number.isNaN(per)) {
    next.qty = String(places * per);
  }
  return next;
}

/* ⚠️ Здесь была карта подписей полей для кнопки «Сканировать в „…"». Кнопку
   сняли, и карта осталась без единственного читателя. Механику скана в поле
   не трогаем: `scanField`, `data-scan-field` и привязка чужих кодов на месте —
   аппаратный сканер ТСД ходит мимо и работает по-прежнему. */

/** Уже принятая в этом приходе партия для той же позиции — или null.
 *
 * Ключ тот же, что у бэка: номенклатура + характеристика + партия поставщика.
 * Когда номер партии ещё неизвестен (голый EAN13, ручной выбор строки), берём
 * позицию по имени — но ТОЛЬКО если такая ровно одна: две партии одного товара
 * в приходе это не редкость, и угадывать, к какой из них добавляют канистру,
 * нельзя. Неоднозначность разрешает человек, вводя номер партии.
 */
function findBatch(batches: BatchView[], draft: BatchDraft): BatchView | null {
  const same = batches.filter(
    (b) =>
      b.nomenclature_ref === draft.nomenclature_ref &&
      (b.characteristic_name || "") === (draft.characteristic_name || ""),
  );
  const number = draft.supplier_batch.trim();
  if (number) return same.find((b) => b.supplier_batch === number) ?? null;
  return same.length === 1 ? (same[0] ?? null) : null;
}

function emptyDraft(): BatchDraft {
  return {
    nomenclature_ref: "",
    nomenclature_name: "",
    characteristic_ref: "",
    characteristic_name: "",
    unit_ref: "",
    unit_type: "",
    unit_name: "",
    unit_coef: 1,
    supplier_batch: "",
    partia_tracked: false,
    production_date: "",
    expiry_date: "",
    qty: "",
    place_type: "канистра",
    places: "1",
    qty_per_place: "",
    pendingCode: "",
  };
}

/** Миниатюра снятого фото с кнопкой удаления.
 *
 *  Ради неё и заводился показ: пока кадров не видно, человек не знает, что
 *  съёмка идёт, и жмёт затвор снова и снова — 80 снимков одного УПД. Удаление
 *  тут же, потому что переснять неудачный кадр надо ДО отправки в 1С.
 */
function PhotoThumb({
  deliveryId,
  photo,
  onDeleted,
  readOnly,
}: {
  deliveryId: string;
  photo: PhotoMeta;
  onDeleted: () => void;
  readOnly: boolean;
}): React.ReactElement {
  const [url, setUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    let dead = false;
    let objectUrl = "";
    void fetchDeliveryPhotoUrl(deliveryId, photo.id).then((u) => {
      if (dead) {
        URL.revokeObjectURL(u);
        return;
      }
      objectUrl = u;
      setUrl(u);
    });
    return () => {
      dead = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [deliveryId, photo.id]);

  const remove = useMutation({
    mutationFn: () => deleteDeliveryPhoto(deliveryId, photo.id),
    onSuccess: onDeleted,
    onError: (e) => toast.error(apiErrorText(e)),
  });

  return (
    <div className="relative size-16 shrink-0 overflow-hidden rounded border bg-slate-100">
      {url ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : (
        <div className="size-full animate-pulse bg-slate-200" />
      )}
      {/* Пометка «в 1С» — про то, что копия ТУДА уже ушла, а не запрет на
          удаление здесь: снимок в портале и вложение в документе — разные
          объекты, и чистят их порознь. */}
      {photo.uploaded && (
        <span
          className="absolute inset-x-0 bottom-0 bg-black/60 text-center text-[9px] leading-4 text-white"
          title="Копия уже отправлена в 1С. Удаление здесь её там НЕ удалит."
        >
          в 1С
        </span>
      )}
      {!readOnly && (
        <button
          type="button"
          onClick={() => remove.mutate()}
          disabled={remove.isPending}
          className="absolute right-0 top-0 rounded-bl bg-black/60 px-1 text-white"
          aria-label="Удалить фото"
        >
          ×
        </button>
      )}
    </div>
  );
}

export function ReceivingDeliveryPage(): React.ReactElement {
  // Чанк камеры тянем при ВХОДЕ: устаревший чанк после выката
  // перезагружает страницу, и делать это в момент тапа «Сканировать»
  // значит прерывать работу ровно посередине.
  usePreloadChunk(CameraScanner);
  const { deliveryId = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const goBack = useGoBack("/receiving");

  const [banner, setBanner] = React.useState<Banner>(null);
  const [blocked, setBlocked] = React.useState<BlockedScan | null>(null);

  // Успех гаснет сам, ошибка и предупреждение — нет.
  //
  // На экране сбора плашка живёт до следующего скана, и это верно: там скан
  // идёт непрерывно. Приёмка устроена иначе — действия редкие (принял партию,
  // напечатал этикетки), следующего скана может не быть полчаса, и подтверждение
  // просто стоит на экране, требуя тапа. А вот ошибку гасить по таймеру нельзя:
  // незамеченный отказ печати — это партия без этикеток, которую обнаружат на
  // навеске.
  React.useEffect(() => {
    if (banner?.kind !== "ok") return;
    const t = window.setTimeout(() => setBanner(null), 4000);
    return () => window.clearTimeout(t);
  }, [banner]);
  const [draft, setDraft] = React.useState<BatchDraft | null>(null);
  // Ошибки НАБРАННЫХ дат (см. `components/DateInput`). Держим отдельно от
  // черновика: в нём лежит только «чистое» ISO-значение, и неверный ввод обязан
  // гасить кнопку, а не молча уезжать в 1С пустой датой.
  const [dateErr, setDateErr] = React.useState({ prod: "", exp: "" });
  React.useEffect(() => {
    if (!draft) setDateErr({ prod: "", exp: "" });
  }, [draft]);
  const [picking, setPicking] = React.useState<ScanResponse | null>(null);
  const [photoFor, setPhotoFor] = React.useState<
    { place: PlaceView; batch: BatchView } | "upd" | "certificate" | null
  >(null);
  const [confirm, setConfirm] = React.useState<null | "delete" | "finish">(null);
  // Камера открывается с ЦЕЛЬЮ: "scan" — общий разбор кода (какая позиция
  // приехала), "field" — положить код в поле карточки.
  //
  // Правило то же, что у аппаратного сканера (`use-scan-capture::isTextEntry`):
  // курсор стоит в текстовом поле — код идёт ТУДА, иначе в общий разбор. Так
  // камера не заводит второй способ ввода, а просто заменяет собой сканер там,
  // где его нет (телефон), и общий путь скана остаётся нетронутым.
  const [cameraFor, setCameraFor] = React.useState<null | "scan" | "field">(null);
  const cameraOpen = cameraFor !== null;
  // Кадры этикетки ПОСТАВЩИКА, снятые прямо в форме приёма. Копятся до тех
  // пор, пока партия не заведена: раньше их не к чему привязать — ни партии,
  // ни мест ещё не существует. Уезжают сразу после приёма, к ПАРТИИ (без
  // места): при «Мест: 45» один кадр не принадлежит ни одному из них, и
  // приписать его «месту 1» значило бы соврать в данных.
  const [labelShots, setLabelShots] = React.useState<{
    open: boolean;
    blobs: Blob[];
  }>({ open: false, blobs: [] });
  // Печать: сколько наклеек нужно физически. Подставляем число мест партии.
  const [printFor, setPrintFor] = React.useState<{
    batchId: string;
    count: string;
  } | null>(null);
  const [scanField, setScanField] = React.useState<string | null>(null);

  React.useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const field = (e.target as HTMLElement | null)?.dataset?.scanField;
      if (field) setScanField(field);
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, []);

  const delivery = useQuery({
    queryKey: ["receiving-delivery", deliveryId],
    queryFn: () => getDelivery(deliveryId),
  });
  const order = useQuery({
    queryKey: ["receiving-order", delivery.data?.order_ref],
    queryFn: () => getReceivingOrder(delivery.data!.order_ref),
    enabled: Boolean(delivery.data?.order_ref),
  });
  const config = useQuery({ queryKey: ["receiving-config"], queryFn: getReceivingConfig });

  const d = delivery.data;
  const readOnly = d?.status !== "open";
  // Приход без документов: УПД вписывают, когда он доедет. Разрешено и у
  // ЗАВЕРШЁННОГО прихода — именно там это и нужно: приняли, провели, товар
  // ушёл в работу, а накладная пришла через неделю.
  const [updOpen, setUpdOpen] = React.useState(false);
  const [updNumber, setUpdNumber] = React.useState("");
  const [updDate, setUpdDate] = React.useState("");
  const [updDateErr, setUpdDateErr] = React.useState("");
  const setUpd = useMutation({
    mutationFn: () =>
      setDeliveryUpd(deliveryId, {
        incoming_number: updNumber.trim(),
        incoming_date: updDate || null,
      }),
    onSuccess: (res) => {
      setUpdOpen(false);
      setUpdNumber("");
      // Сервер вернул приход целиком — кладём его в кэш ВМЕСТО повторного GET
      // (`refresh()` здесь и был бы этим GET'ом).
      qc.setQueryData(["receiving-delivery", deliveryId], res.delivery);
      setBanner({
        kind: res.warnings.length ? "warn" : "ok",
        text: res.warnings.length
          ? `УПД ${res.delivery.incoming_number} вписан. ${res.warnings.join(" ")}`
          : `УПД ${res.delivery.incoming_number} вписан везде: приход, ` +
            "накладная 1С и задачи проверки.",
      });
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["receiving-delivery", deliveryId] });

  /** Принято по строке заказа В ЭТОМ приходе — сумма партий по (ном, хар).
   *
   *  Считаем на экране, а не на бэке: данные уже здесь и обновляются сразу
   *  после приёма, без второго запроса. Единица общая — единица строки заказа
   *  (её же несёт партия), поэтому числа складываются напрямую. */
  const acceptedFor = (line: OrderLineView): number =>
    (d?.batches ?? [])
      .filter(
        (b) =>
          b.nomenclature_ref === line.nomenclature_ref &&
          (b.characteristic_name || "") === (line.characteristic_name || ""),
      )
      .reduce((n, b) => n + Number(b.qty || 0), 0);

  /** Снимки ЭТОЙ позиции: паспорт качества привязан к партии, этикетка — к
   *  месту. Обе привязки приезжают в `PhotoMeta`, так что фильтр местный и
   *  лишнего запроса не стоит. */
  const photosOf = (batch: { id: string; places: { id: string }[] }) => {
    const places = new Set(batch.places.map((pl) => pl.id));
    return (d?.photos ?? []).filter(
      (ph) => ph.batch_id === batch.id || (ph.place_id && places.has(ph.place_id)),
    );
  };

  // Скан не должен проглатываться, пока открыта шторка или камера: там у
  // человека другой контекст, и код надо класть в поле, а не в общий разбор.
  // Камера гасит перехват отдельно — иначе один код придёт дважды.
  const captureEnabled =
    Boolean(d) && !readOnly && !draft && !picking && !photoFor && !confirm && !cameraOpen;

  // Партия, к которой добор пойдёт местом. Считаем из СВЕЖИХ данных прихода,
  // а не запоминаем при открытии карточки: между сканами приход перечитывается,
  // и сосед мог принять эту позицию, пока карточка открыта.
  const draftBatch = draft ? findBatch(d?.batches ?? [], draft) : null;

  // Почему печать недоступна — ВИДИМОЙ строкой, а не всплывающей подсказкой:
  // приёмку ведут с телефона и ТСД, где hover не существует, и погашенная
  // кнопка без причины читается как поломка портала. Условий два и они
  // независимы: адрес принтера и макет этикетки.
  const printBlocked = config.data
    ? [
        config.data.printer_ready ? "" : "принтер приёмки не настроен",
        config.data.label_template_ready ? "" : "макет этикетки не заполнен",
      ].filter(Boolean)
    : [];
  const canPrint = Boolean(config.data) && printBlocked.length === 0;

  const scanMethod = config.data?.scan_method ?? "tsd";
  const cameraAllowed = scanMethod === "camera" || scanMethod === "both";
  useBackGuard(cameraOpen, () => setCameraFor(null));

  const scan = useMutation({
    mutationFn: (code: string) => scanCode(deliveryId, code),
    onSuccess: (res) => {
      if (res.nomenclature_ref) {
        const line =
          order.data?.lines.find(
            (l: OrderLineView) =>
              l.nomenclature_ref === res.nomenclature_ref &&
              (!res.characteristic_ref ||
                l.characteristic_ref === res.characteristic_ref),
          ) ?? null;
        openDraft(line, res);
      } else {
        // Код незнакомый — не ошибка и не догадка: спрашиваем, что это.
        setPicking(res);
      }
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const capture = useScanCapture({
    enabled: captureEnabled,
    onScan: (raw) => {
      setBlocked(null);
      scan.mutate(raw);
    },
    // Код пришёл, пока перехват выключен: не теряем молча — называем, почему
    // он не принят. Закрытый приход сканировать нечем.
    onBlocked: (raw) => {
      if (!d || readOnly) return;
      const reason = draft
        ? "Сначала примите или закройте карточку позиции."
        : picking
          ? "Сначала выберите, что это за код, или закройте окно."
          : photoFor
            ? "Сначала закройте окно фото."
            : cameraOpen
              ? "Открыта камера — закройте её, чтобы сканировать сканером."
              : "Сначала закройте открытое окно.";
      setBlocked({ raw, reason });
    },
  });

  function openDraft(line: OrderLineView | null, res?: ScanResponse): void {
    const next = emptyDraft();
    if (line) {
      next.partia_tracked = line.partia_tracked;
      next.nomenclature_ref = line.nomenclature_ref;
      next.nomenclature_name = line.nomenclature_name;
      next.characteristic_ref = line.characteristic_ref;
      next.characteristic_name = line.characteristic_name;
      // Количество в строке заказа — В ЕДИНИЦЕ СТРОКИ, поэтому единицу несём
      // вместе с ним: иначе документ получит число коробов при единице «шт».
      next.unit_ref = line.unit_ref;
      next.unit_type = line.unit_type;
      next.unit_name = line.unit_name;
      next.unit_coef = line.unit_coef;
      next.qty = String(line.qty_remaining ?? line.qty);
    }
    // Номер партии поставщика: у ТАРЫ подставляем номер УПД (партия там —
    // ярлык поставки), у УЧЁТНЫХ категорий поле остаётся пустым и обязательным
    // — по такой партии идут срок годности и FEFO, и номер накладной в этой
    // роли это ложь. Признак приезжает со строкой заказа и с ответом скана.
    if (line && !line.partia_tracked) next.supplier_batch = d?.incoming_number ?? "";
    if (res) {
      if (!res.partia_tracked && !res.supplier_batch) {
        next.supplier_batch = d?.incoming_number ?? "";
      }
      next.partia_tracked = res.partia_tracked;
      next.nomenclature_ref = res.nomenclature_ref || next.nomenclature_ref;
      next.nomenclature_name = res.nomenclature_name || next.nomenclature_name;
      next.characteristic_ref = res.characteristic_ref || next.characteristic_ref;
      next.characteristic_name =
        res.characteristic_name || next.characteristic_name;
      if (res.supplier_batch) next.supplier_batch = res.supplier_batch;
      next.production_date = res.production_date || "";
      next.expiry_date = res.expiry_date || "";
      // Запоминаем ТОВАРНУЮ часть кода, а не всю строку скана: правило одно и
      // живёт на бэке (`ScanResult.binding_code`) — им же ищется привязка.
      if (!res.matched_by) next.pendingCode = res.binding_code || res.raw;
    }
    // Позиция уже принята в этом приходе — карточка становится «добавить
    // место»: партия и даты закреплены заведённой в 1С партией, менять их
    // тут нечем, а место просто дописывается к ней.
    const prev = findBatch(d?.batches ?? [], next);
    if (prev) {
      next.supplier_batch = prev.supplier_batch;
      next.production_date = prev.production_date ?? "";
      next.expiry_date = prev.expiry_date ?? "";
      next.place_type = prev.places[0]?.place_type || next.place_type;
      next.qty_per_place = String(prev.places[0]?.qty_declared ?? "");
      next.places = "1";
      next.qty = String(prev.places[0]?.qty_declared ?? "");
    }
    // Цель скана сбрасываем вместе с карточкой: иначе камера метила бы в
    // поле, которого на новой позиции человек ещё не касался.
    setScanField(null);
    setDraft(next);
  }

  const accept = useMutation({
    mutationFn: async (draftValue: BatchDraft) => {
      if (draftValue.pendingCode && draftValue.nomenclature_ref) {
        await bindBarcode({
          delivery_id: deliveryId,
          code: draftValue.pendingCode,
          nomenclature_ref: draftValue.nomenclature_ref,
          nomenclature_name: draftValue.nomenclature_name,
        });
      }
      return acceptBatch(deliveryId, {
        nomenclature_ref: draftValue.nomenclature_ref,
        characteristic_ref: draftValue.characteristic_ref || undefined,
        characteristic_name: draftValue.characteristic_name || undefined,
        unit_ref: draftValue.unit_ref || undefined,
        unit_type: draftValue.unit_type || undefined,
        unit_name: draftValue.unit_name || undefined,
        unit_coef: draftValue.unit_coef,
        supplier_batch: draftValue.supplier_batch.trim(),
        production_date: draftValue.production_date || null,
        expiry_date: draftValue.expiry_date || null,
        qty: Number(draftValue.qty) || 0,
        place_type: draftValue.place_type,
        places: Number(draftValue.places) || 0,
        qty_per_place: draftValue.qty_per_place ? Number(draftValue.qty_per_place) : null,
      });
    },
    onSuccess: async (batch) => {
      const added = Boolean(draftBatch);
      setDraft(null);
      // ⚠️ Кадры отправляем ЗДЕСЬ, а не в отдельном заходе: партия только что
      // появилась, и это первый момент, когда фото есть к чему привязать.
      // Отказ отправки — громкая плашка, а кадры остаются в форме: молча
      // потерять снимок нельзя, второй раз этикетку уже не сфотографируют.
      if (labelShots.blobs.length) {
        const failed: Blob[] = [];
        for (const blob of labelShots.blobs) {
          try {
            await uploadDeliveryPhoto(deliveryId, blob, {
              category: "goods",
              batchId: batch.id,
            });
          } catch {
            failed.push(blob);
          }
        }
        setLabelShots({ open: false, blobs: failed });
        if (failed.length) {
          setBanner({
            kind: "error",
            text:
              `Партия принята, но ${failed.length} ` +
              `${plural(failed.length, ["фото", "фото", "фото"])} не отправилось. ` +
              "Кадры сохранены в форме — откройте позицию и повторите.",
          });
          refresh();
          return;
        }
      }
      setBanner({
        kind: "ok",
        // У непартионных позиций партии в 1С нет — говорить «партия 12 заведена»
        // там нельзя: кладовщик пойдёт искать её в справочнике и не найдёт.
        text: added
          ? `Место добавлено: ${batchLabel(batch)} теперь ${batch.places.length} ` +
            `${plural(batch.places.length, ["место", "места", "мест"])}, ${batch.qty} ${batch.unit_name}.`
          : batch.partia_number
            ? `Партия ${batch.partia_number} заведена, штрихкод ${batch.barcode}. Напечатайте этикетки.`
            : `Позиция принята, штрихкод ${batch.barcode}. Напечатайте этикетки.`,
      });
      refresh();
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const print = useMutation({
    mutationFn: (arg: { batchId: string; count: number }) =>
      printBatchLabels(deliveryId, arg.batchId, arg.count),
    onSuccess: (r) => {
      setPrintFor(null);
      setBanner({
        kind: "ok",
        text: `Отправлено этикеток: ${r.printed}. Клейте рядом с этикеткой поставщика и фотографируйте каждое место.`,
      });
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
    onSettled: refresh,
  });

  /** Перечитать снимок номенклатуры из 1С — артикул, дозаполненный ПОСЛЕ
   *  приёма, иначе не попадёт на перепечатку. Доступно и на завершённом
   *  приходе (как печать): в 1С здесь только читают. */
  const refreshSnapshot = useMutation({
    mutationFn: (batchId: string) => refreshBatchSnapshot(deliveryId, batchId),
    onSuccess: (r) =>
      setBanner({
        kind: "ok",
        text: r.changed.length
          ? `Обновлено из 1С: ${r.changed.join(", ")}.`
          : "В 1С то же самое, менять нечего.",
      }),
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
    onSettled: refresh,
  });

  const removeBatch = useMutation({
    mutationFn: (batchId: string) => deleteBatch(deliveryId, batchId),
    onSuccess: (r) =>
      setBanner(
        r.warnings.length
          ? { kind: "warn", text: r.warnings.join(" ") }
          : { kind: "ok", text: "Партия удалена." },
      ),
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
    onSettled: refresh,
  });

  // Процент отправки снимка — общий хук (см. `use-upload-progress`). ⚠️ Без
  // индикации экран не отличал «грузится» от «зависло», и кладовщики снимали
  // по многу раз: приёмка — самый фото-интенсивный раздел.
  const photoProgress = useUploadProgress();

  const photo = useMutation({
    mutationFn: (args: { blob: Blob; category: "upd" | "certificates" | "goods"; placeId?: string; batchId?: string }) =>
      uploadDeliveryPhoto(deliveryId, args.blob, {
        category: args.category,
        placeId: args.placeId,
        batchId: args.batchId,
        onProgress: photoProgress.start(),
      }),
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
    onSettled: () => {
      photoProgress.reset();
      refresh();
    },
  });

  const finish = useMutation({
    mutationFn: () => finishDelivery(deliveryId),
    onSuccess: (r) => {
      setConfirm(null);
      const doc = r.receipt_number
        ? `Приходная накладная ${r.receipt_number} создана (не проведена).`
        : "";
      // Кто именно проверяет — важнее номера задачи: кладовщик должен видеть,
      // что товар кому-то передан, а не что «что-то улетело в Битрикс».
      const tasks = (r.bitrix_task_info ?? [])
        .map((t) => `${t.target_title} — ${t.responsible} (задача №${t.task_id})`)
        .join("; ");
      setBanner(
        r.bitrix_warning
          ? {
              // Приход закрыт, документы есть — но задача не ушла. Это
              // предупреждение, а не успех: оно не должно гаснуть само.
              kind: "warn",
              text: `Приход закрыт. ${doc} ${r.bitrix_warning}`.trim(),
            }
          : {
              kind: "ok",
              text: `Приход закрыт. ${doc}${
                tasks ? ` На проверку: ${tasks}.` : ""
              }`.trim(),
            },
      );
      refresh();
    },
    onError: (e) => {
      setConfirm(null);
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  // Повторная постановка задач на проверку у завершённого прихода: ставятся
  // только недостающие задачи Битрикса + досоздаётся проверка ОКК. Сбой 1С —
  // 502 с текстом сервера, показываем как есть.
  const retryTasks = useMutation({
    mutationFn: () => retryInspectionTasks(deliveryId),
    onSuccess: (r) => {
      const tasks = (r.bitrix_task_info ?? [])
        .map((t) => `${t.target_title} — ${t.responsible} (задача №${t.task_id})`)
        .join("; ");
      setBanner(
        r.bitrix_warning
          ? { kind: "warn", text: `Задачи поставлены не все. ${r.bitrix_warning}` }
          : {
              kind: "ok",
              text: tasks ? `Задачи на проверку: ${tasks}.` : "Задачи на проверку поставлены.",
            },
      );
      refresh();
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const post = useMutation({
    mutationFn: () => postDeliveryReceipt(deliveryId),
    onSuccess: () => setBanner({ kind: "ok", text: "Приходная накладная проведена." }),
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
    onSettled: refresh,
  });

  const reopen = useMutation({
    mutationFn: () => reopenDelivery(deliveryId),
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
    onSettled: refresh,
  });

  const dropDelivery = useMutation({
    mutationFn: () => deleteDelivery(deliveryId),
    onSuccess: (r) => {
      if (r.warnings.length) window.alert(r.warnings.join("\n"));
      navigate("/receiving", { replace: true });
    },
    onError: (e) => {
      setConfirm(null);
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  // Одно определение «место без фото» на экран: очередь и счётчик берутся из
  // одного списка, а не считаются двумя независимыми способами.
  const missing = React.useMemo(
    () =>
      (d?.batches ?? []).flatMap((batch) =>
        batch.places.filter((p) => !p.has_photo).map((place) => ({ batch, place })),
      ),
    [d],
  );
  const missingPhotos = missing.length;
  const nextPlace = missing[0] ?? null;

  return (
    <div className="flex h-app flex-col bg-slate-100">
      <input ref={capture.scanInputRef} {...capture.scanInputProps} className="sr-only" />

      <header className="flex items-center gap-3 bg-white px-3 py-2 shadow-sm">
        <button onClick={goBack} className="text-sm text-slate-500">
          ←
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {d ? updTitle(d) : "Приход"}
          </div>
          <div className="truncate text-xs text-slate-500">
            {d ? `${d.order_number} · ${d.kontragent_name ?? ""}` : ""}
          </div>
        </div>
        {d?.upd_missing && (
          <Button size="sm" variant="outline" onClick={() => setUpdOpen(true)}>
            Указать УПД
          </Button>
        )}
        {readOnly && (
          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">
            завершён
          </span>
        )}
        {d && <OkkBadge okk={d.okk} className="text-xs" />}
      </header>

      {updOpen && (
      <BottomSheet onClose={() => setUpdOpen(false)} panelClassName="p-4">
        <div className="mb-1 text-sm font-semibold">Настоящий номер УПД</div>
        <p className="mb-3 text-xs text-slate-500">
          Портал впишет его сразу везде: в приход, в номер партии поставщика у
          позиций без партионного учёта, в приходную накладную 1С (даже
          проведённую) и в задачи проверки.
        </p>
        <label className="block text-[11px] text-slate-500">
          Номер УПД
          <input
            autoFocus
            className="mt-1 w-full rounded border p-2 text-sm"
            value={updNumber}
            onChange={(e) => setUpdNumber(e.target.value)}
            placeholder="напр. 4417"
          />
        </label>
        <label className="mt-3 block text-[11px] text-slate-500">
          Дата УПД
          <DateInput
            className="mt-1"
            ariaLabel="Дата УПД"
            value={updDate}
            onChange={(iso, err) => {
              setUpdDate(iso);
              setUpdDateErr(err);
            }}
          />
        </label>
        <Button
          className="mt-4 w-full"
          disabled={setUpd.isPending || !updNumber.trim() || Boolean(updDateErr)}
          onClick={() => setUpd.mutate()}
        >
          Вписать
        </Button>
      </BottomSheet>
      )}

      {(delivery.isLoading || delivery.error) && (
        <LoadState
          isLoading={delivery.isLoading}
          error={delivery.error}
          onRetry={() => void delivery.refetch()}
        />
      )}
      {d && (
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Плашка — ПОД шапкой, обычным элементом потока, а не поверх низа
                экрана: `ScanBannerSlot` прижимает её к `bottom-0`, и на этом
                экране она ложилась на нижнюю панель, перекрывая «Завершить» и
                «Фото места». Здесь она ничего не закрывает и сдвигает список. */}
            {banner && (
              <div className="px-3 pt-3">
                <ScanBanner
                  tone={banner.kind}
                  title={banner.text}
                  onClose={() => setBanner(null)}
                />
              </div>
            )}
            {blocked && (
              <div className="px-3 pt-3">
                <BlockedScanBanner blocked={blocked} onClose={() => setBlocked(null)} />
              </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {/* Позиции заказа — основной путь: тап по строке открывает карточку. */}
              {!readOnly && (
                <section className="mb-4">
                  <h2 className="mb-2 text-xs font-semibold uppercase text-slate-500">
                    Ожидается по заказу
                  </h2>
                  <div className="space-y-1">
                    {order.data?.lines.map((line: OrderLineView) => (
                      <button
                        key={`${line.nomenclature_ref}-${line.characteristic_ref}`}
                        onClick={() => openDraft(line)}
                        className="flex w-full items-center gap-2 rounded-lg bg-white p-3 text-left shadow-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {line.nomenclature_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {line.nomenclature_article}
                            {line.characteristic_name ? ` · ${line.characteristic_name}` : ""}
                          </div>
                        </div>
                        <LineProgress line={line} accepted={acceptedFor(line)} />
                        <Plus className="h-4 w-4 shrink-0 text-slate-400" />
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Принятые партии */}
              <section className="mb-4">
                <h2 className="mb-2 text-xs font-semibold uppercase text-slate-500">
                  Принято ({d.batches.length})
                </h2>
                {printBlocked.length > 0 && (
                  <p className="mb-2 rounded-md bg-amber-100 px-2 py-1.5 text-[11px] text-amber-900">
                    Этикетки не печатаются: {printBlocked.join(", ")}. Попросите
                    администратора настроить это в системных настройках —
                    принимать позиции можно и сейчас, партии заводятся в 1С.
                  </p>
                )}
                <div className="space-y-2">
                  {d.batches.map((batch) => (
                    <div key={batch.id} className="rounded-lg bg-white p-3 shadow-sm">
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {batch.nomenclature_name}
                          </div>
                          {/* Артикул виден в карточке, потому что иначе
                              результат «Обновить из 1С» некуда посмотреть: на
                              этикетку он попадает из этого же снимка. */}
                          <div className="text-xs text-slate-500">
                            {batch.nomenclature_article || "—"} · поставщик{" "}
                            {batch.supplier_batch || "—"} · {batch.qty}{" "}
                            {batch.unit_name}
                            {partiaSuffix(batch)}
                          </div>
                          <div className="text-xs text-slate-400">
                            {batch.barcode}
                            {batch.role === "label" && " · ярлык (без партионного учёта)"}
                          </div>
                        </div>
                        {/* ⚠️ Печать НЕ прячется на завершённом приходе, в
                            отличие от удаления. Этикетка отклеилась, порвалась
                            или промокла — это выясняется, когда приход давно
                            закрыт, а копия ничего в учёте не меняет: тот же
                            штрихкод той же партии. Раньше кнопка лежала в одном
                            условии с удалением и пропадала заодно с ним, и
                            единственным путём оставалось «Вернуть в работу» —
                            то есть пометить приходную накладную в 1С на
                            удаление ради наклейки. */}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!canPrint || print.isPending}
                          title={
                            canPrint
                              ? "Напечатать этикетки"
                              : `Печать недоступна: ${printBlocked.join(", ")}`
                          }
                          onClick={() =>
                            setPrintFor({
                              batchId: batch.id,
                              // По умолчанию — по одной на место, как было.
                              count: String(batch.places.length || 1),
                            })
                          }
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        {/* Тоже без гейта по `readOnly`: артикул дозаполняют в
                            1С после приёма, а перепечатывают этикетку как раз
                            на закрытом приходе. */}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={
                            refreshSnapshot.isPending &&
                            refreshSnapshot.variables === batch.id
                          }
                          title="Обновить данные номенклатуры из 1С"
                          onClick={() => refreshSnapshot.mutate(batch.id)}
                        >
                          <RefreshCw
                            className={
                              "h-4 w-4" +
                              (refreshSnapshot.isPending &&
                              refreshSnapshot.variables === batch.id
                                ? " animate-spin"
                                : "")
                            }
                          />
                        </Button>
                        {!readOnly && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeBatch.mutate(batch.id)}
                          >
                            <Trash2 className="h-4 w-4 text-rose-600" />
                          </Button>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {batch.places.map((place) => (
                          <button
                            key={place.id}
                            disabled={readOnly}
                            onClick={() => setPhotoFor({ place, batch })}
                            className={
                              "rounded px-2 py-1 text-xs " +
                              (place.has_photo
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-amber-100 text-amber-900")
                            }
                          >
                            {place.seq}
                            {place.has_photo ? " ✓" : " 📷"}
                          </button>
                        ))}
                      </div>
                      {/* Миниатюры ПОД позицией: «место 3 ✓» говорит, что кадр
                          есть, но не говорит, ЧТО на нём — а снимают этикетку
                          вслепую, вытянутой рукой. Видно сразу — переснимают
                          сразу, а не после финиша. */}
                      {photosOf(batch).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {photosOf(batch).map((ph) => (
                            <PhotoThumb
                              key={ph.id}
                              deliveryId={deliveryId}
                              photo={ph}
                              readOnly={readOnly}
                              onDeleted={() => void refresh()}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {d.batches.length === 0 && (
                    <p className="rounded-lg bg-white p-3 text-sm text-slate-500 shadow-sm">
                      Ничего не принято. Отсканируйте тару или выберите позицию из заказа.
                    </p>
                  )}
                </div>
              </section>

              {/* Снятые кадры ВИДНЫ и удаляются по одному. Голый счётчик этого
                  не давал: человек не понимал, снялось ли, и щёлкал вслепую. */}
              {(
                [
                  ["upd", "Фото УПД", "снять УПД"],
                  ["certificates", "Паспорта качества", "снять паспорт"],
                ] as const
              ).map(([cat, title, action]) => {
                const shots = d.photos.filter((p) => p.category === cat);
                return (
                  <section key={cat} className="mb-3">
                    <div className="mb-1 flex items-center gap-2 text-xs text-slate-500">
                      <span>
                        {title}: <b className="text-slate-700">{shots.length}</b>
                        {shots.some((p) => p.uploaded) && (
                          <span className="ml-1 text-slate-400">
                            (копия в 1С: {shots.filter((p) => p.uploaded).length};
                            удаление здесь её в 1С не тронет)
                          </span>
                        )}
                      </span>
                      {!readOnly && (
                        <button
                          className="underline"
                          onClick={() =>
                            setPhotoFor(cat === "upd" ? "upd" : "certificate")
                          }
                        >
                          {action}
                        </button>
                      )}
                    </div>
                    {shots.length > 0 && (
                      <div className="flex gap-1.5 overflow-x-auto pb-1">
                        {shots.map((p) => (
                          <PhotoThumb
                            key={p.id}
                            deliveryId={deliveryId}
                            photo={p}
                            readOnly={readOnly}
                            onDeleted={refresh}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}

              {d.receipt_number && (
                <section className="rounded-lg bg-white p-3 text-sm shadow-sm">
                  Приходная накладная <b>{d.receipt_number}</b>{" "}
                  {d.receipt_posted ? "· проведена" : "· не проведена"}
                  {!d.receipt_posted && (
                    <Button
                      size="sm"
                      className="ml-2"
                      disabled={post.isPending}
                      onClick={() => post.mutate()}
                    >
                      Провести
                    </Button>
                  )}
                </section>
              )}
            </div>

            {/* Нижняя панель. Кнопки в СЕТКЕ, а не в ряд: на телефоне четыре
                кнопки в строку не помещались, и правая («Фото места») уезжала
                за край вместе со счётчиком. Сетка переносит их на две строки,
                ширина остаётся пальцевой. `pb-[env(safe-area-inset-bottom)]` —
                чтобы панель не пряталась под системной полосой жестов. */}
            <div className="border-t bg-white p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {readOnly ? (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    className="h-12"
                    disabled={retryTasks.isPending}
                    title="Поставит недостающие задачи на проверку в Битрикс и задание ОКК"
                    onClick={() => retryTasks.mutate()}
                  >
                    Поставить задачу
                  </Button>
                  <Button variant="outline" className="h-12" onClick={() => reopen.mutate()}>
                    Вернуть в работу
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {cameraAllowed && (
                    <Button
                      variant="outline"
                      className="h-12"
                      onClick={() => setCameraFor("scan")}
                      title="Сканировать штрихкод или DataMatrix камерой"
                    >
                      <ScanLine className="mr-1 h-4 w-4 shrink-0" />
                      <span className="truncate">Сканировать</span>
                    </Button>
                  )}
                  {nextPlace && (
                    <Button
                      variant="outline"
                      className="h-12"
                      onClick={() => setPhotoFor(nextPlace)}
                    >
                      <Camera className="mr-1 h-4 w-4 shrink-0" />
                      <span className="truncate">Фото места ({missingPhotos})</span>
                    </Button>
                  )}
                  <Button
                    className="col-span-2 h-12"
                    disabled={finish.isPending}
                    onClick={() => setConfirm("finish")}
                  >
                    <Check className="mr-1 h-4 w-4 shrink-0" />
                    Завершить приём
                  </Button>
                  <button
                    type="button"
                    onClick={() => setConfirm("delete")}
                    className="col-span-2 py-1 text-center text-xs text-red-600"
                  >
                    Удалить приход
                  </button>
                </div>
              )}
            </div>
          </div>
      )}

      {cameraOpen && (
        <React.Suspense fallback={null}>
          {/* Выше шторки (`z-50`) и диалога подтверждения: камеру открывают ИЗ
              карточки, и под шторкой её просто не было бы видно. */}
          <div className="fixed inset-0 z-[80] bg-black">
            <CameraScanner
              onDetect={(raw) => {
                const target = cameraFor;
                setCameraFor(null);
                if (target === "field") {
                  const field = scanField ?? "supplier_batch";
                  setDraft((prev) =>
                    prev ? withTotal(prev, { [field]: raw } as Partial<BatchDraft>) : prev,
                  );
                } else {
                  scan.mutate(raw);
                }
              }}
              onClose={() => setCameraFor(null)}
            />
          </div>
        </React.Suspense>
      )}

      {/* Незнакомый код: спрашиваем, что это, вместо угадывания */}
      {picking && (
        <BottomSheet onClose={() => setPicking(null)}>
          <div className="max-h-[70dvh] overflow-y-auto p-4">
            <h3 className="text-base font-semibold">Код не знаком</h3>
            <p className="mt-1 text-sm text-slate-600">
              <code>{picking.raw}</code> у этого поставщика не встречался. Выберите
              позицию — портал запомнит код и в следующий раз опознает сам.
            </p>
            <div className="mt-3 space-y-1">
              {order.data?.lines.map((line: OrderLineView) => (
                <button
                  key={line.nomenclature_ref}
                  className="w-full rounded-lg bg-slate-50 p-3 text-left text-sm"
                  onClick={() => {
                    const res = picking;
                    setPicking(null);
                    openDraft(line, res);
                  }}
                >
                  {line.nomenclature_name}
                  <span className="block text-xs text-slate-500">
                    {line.nomenclature_article}
                  </span>
                </button>
              ))}
            </div>
            <Button variant="ghost" className="mt-3 w-full" onClick={() => setPicking(null)}>
              Это не товар
            </Button>
          </div>
        </BottomSheet>
      )}

      {/* Карточка партии */}
      {draft && (
        <BottomSheet onClose={() => setDraft(null)}>
          {/* `max-h-[80svh]`, а не `dvh`: на телефоне клавиатура ужимает
              видимую область, и шторка на 85dvh уезжала под неё вместе с
              полем партии. `svh` считает по МАЛОЙ высоте вьюпорта — то есть
              как раз по той, что остаётся с открытой клавиатурой. */}
          <div className="max-h-[80svh] overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <h3 className="text-base font-semibold">{draft.nomenclature_name || "Позиция"}</h3>
            {draft.pendingCode && (
              <p className="mt-1 text-xs text-emerald-700">
                Код {draft.pendingCode} будет запомнен за этой позицией.
              </p>
            )}
            {draftBatch && (
              <p className="mt-1 rounded bg-sky-50 px-2 py-1 text-xs text-sky-900">
                Уже принято: {draftBatch.places.length}{" "}
                {plural(draftBatch.places.length, ["место", "места", "мест"])},{" "}
                {draftBatch.qty} {draftBatch.unit_name}
                {partiaSuffix(draftBatch)}. Новое место добавится к этой позиции.
              </p>
            )}
            <div className="mt-3 space-y-3">
              <Field
                label={
                  draft.partia_tracked
                    ? "Партия поставщика (обязательно)"
                    : "Партия поставщика"
                }
              >
                <input
                  data-scan-field="supplier_batch"
                  autoFocus={draft.partia_tracked}
                  onFocus={(e) =>
                    // Поле у нижнего края шторки: без этого клавиатура
                    // закрывает его целиком, и человек печатает вслепую.
                    setTimeout(
                      () => e.target.scrollIntoView({ block: "center" }),
                      250,
                    )
                  }
                  className={
                    "w-full rounded border p-2 " +
                    (draft.partia_tracked && !draft.supplier_batch.trim()
                      ? "border-amber-500 bg-amber-50"
                      : "")
                  }
                  value={draft.supplier_batch}
                  placeholder={
                    draft.partia_tracked
                      ? "номер с этикетки поставщика"
                      : `пусто → номер УПД ${d?.incoming_number ?? ""}`
                  }
                  onChange={(e) => setDraft({ ...draft, supplier_batch: e.target.value })}
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Дата производства">
                  <DateInput
                    ariaLabel="Дата производства"
                    value={draft.production_date}
                    onChange={(iso, err) => {
                      setDraft({ ...draft, production_date: iso });
                      setDateErr((p) => ({ ...p, prod: err }));
                    }}
                  />
                </Field>
                <Field label="Годен до">
                  <DateInput
                    ariaLabel="Годен до"
                    value={draft.expiry_date}
                    onChange={(iso, err) => {
                      setDraft({ ...draft, expiry_date: iso });
                      setDateErr((p) => ({ ...p, exp: err }));
                    }}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Тара">
                  {/* `h-[42px]` — высота нативного select не совпадает с
                      инпутами (у него нет наших вертикальных паддингов), и в
                      строке из трёх полей он был заметно ниже соседей. */}
                  <select
                    className="h-[42px] w-full rounded border bg-white px-2"
                    value={draft.place_type}
                    onChange={(e) => setDraft({ ...draft, place_type: e.target.value })}
                  >
                    {PLACE_TYPES.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Мест">
                  <input
                    type="number"
                    inputMode="numeric"
                    className="w-full rounded border p-2"
                    value={draft.places}
                    onChange={(e) => setDraft(withTotal(draft, { places: e.target.value }))}
                  />
                </Field>
                <Field label="Количество в 1 месте">
                  <input
                    data-scan-field="qty_per_place"
                    type="number"
                    inputMode="decimal"
                    className="w-full rounded border p-2"
                    value={draft.qty_per_place}
                    onChange={(e) =>
                      setDraft(withTotal(draft, { qty_per_place: e.target.value }))
                    }
                  />
                </Field>
              </div>
              <Field label={`Всего принято${draft.unit_name ? `, ${draft.unit_name}` : ""}`}>
                <input
                  data-scan-field="qty"
                  type="number"
                  inputMode="decimal"
                  className="w-full rounded border p-2 text-lg font-semibold"
                  value={draft.qty}
                  onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
                />
              </Field>
            </div>
            {/* ⚠️ Здесь была кнопка «Сканировать в „Партия поставщика"» — снята
                по просьбе склада. Механику скана в поле не трогаем
                (`setCameraFor("field")`, `SCAN_FIELDS`, привязка чужих кодов):
                аппаратный сканер ТСД ходит мимо неё и работает по-прежнему.

                На её месте — съёмка этикетки ПОСТАВЩИКА прямо в форме. Кадры
                копятся до приёма: раньше их некуда привязать — ни партии, ни
                мест ещё нет. */}
            <Button
              variant="outline"
              className="mt-3 w-full"
              onClick={() => setLabelShots((s) => ({ ...s, open: true }))}
            >
              <Camera className="mr-1 h-4 w-4" />
              {labelShots.blobs.length
                ? `Фото этикетки · ${labelShots.blobs.length}`
                : "Фото этикетки"}
            </Button>
            <Button
              className="mt-4 w-full"
              disabled={
                !draft.nomenclature_ref ||
                accept.isPending ||
                Boolean(dateErr.prod || dateErr.exp) ||
                // Учётная партия без номера поставщика — отказ и на бэке
                // (по ней идут срок годности и FEFO). Гасим кнопку здесь,
                // чтобы человек не бился в ошибку после заполнения формы.
                (draft.partia_tracked && !draft.supplier_batch.trim())
              }
              onClick={() => accept.mutate(draft)}
            >
              {accept.isPending
                ? draftBatch
                  ? "Добавляем место…"
                  : "Заводим партию в 1С…"
                : draftBatch
                  ? "Добавить место"
                  : "Принять партию"}
            </Button>
          </div>
        </BottomSheet>
      )}

      {/* Камера формы приёма: снимаем этикетку ПОСТАВЩИКА до того, как партия
          заведена. Кадр никуда не уходит сразу — копится и уезжает вместе с
          приёмом позиции. */}
      {labelShots.open && (
        <PhotoCapture
          title={`Фото этикетки · снято: ${labelShots.blobs.length}`}
          onClose={() => setLabelShots((s) => ({ ...s, open: false }))}
          onCapture={async (blob) => {
            setLabelShots((s) => ({ ...s, blobs: [...s.blobs, blob] }));
          }}
        />
      )}

      {/* Сколько наклеек печатать. Число мест — подсказка, а не закон: одно
          «место» на складе часто означает всю партию, а коробок в ней сорок
          пять, и до этого ту же кнопку жали сорок пять раз. */}
      {printFor && (
        <Dialog
          title="Сколько этикеток печатать"
          body={
            <div>
              <p className="text-xs text-slate-500">
                Подставлено число принятых мест. Поправьте, если наклеек нужно
                больше или меньше.
              </p>
              <input
                autoFocus
                type="number"
                inputMode="numeric"
                min={1}
                aria-label="Количество этикеток"
                className="mt-3 w-full rounded border p-2 text-lg font-semibold"
                value={printFor.count}
                onChange={(e) => setPrintFor({ ...printFor, count: e.target.value })}
              />
            </div>
          }
          confirmLabel="Напечатать"
          confirmBusy={print.isPending}
          onConfirm={() =>
            Number(printFor.count) >= 1 &&
            print.mutate({
              batchId: printFor.batchId,
              count: Number(printFor.count),
            })
          }
          onCancel={() => setPrintFor(null)}
        />
      )}

      {/* Камера */}
      {photoFor && (
        <PhotoCapture
          title={
            photoFor === "upd"
              ? `Фото УПД · снято: ${(d?.photos ?? []).filter((p) => p.category === "upd").length}`
              : photoFor === "certificate"
                ? `Паспорт качества · снято: ${
                    (d?.photos ?? []).filter((p) => p.category === "certificates").length
                  }`
                : `Место ${photoFor.place.seq} из ${photoFor.batch.places.length} — снимите обе этикетки в одном кадре`
          }
          // ⚠️ `busy` здесь НЕ передавался вовсе — кнопка «Снять» оставалась
          // активной всё время отправки, и ничто не показывало, что снимок уже
          // ушёл. Приёмка — самый фото-интенсивный раздел, отсюда и жалоба на
          // пачки одинаковых кадров.
          busy={photo.isPending}
          progress={photoProgress.progress}
          onClose={() => setPhotoFor(null)}
          onCapture={async (blob) => {
            if (photoFor === "upd") await photo.mutateAsync({ blob, category: "upd" });
            else if (photoFor === "certificate")
              await photo.mutateAsync({ blob, category: "certificates" });
            else
              await photo.mutateAsync({
                blob,
                category: "goods",
                placeId: photoFor.place.id,
                batchId: photoFor.batch.id,
              });
          }}
        />
      )}

      {confirm === "finish" && (
        <Dialog
          title="Завершить приход?"
          body={
            missingPhotos
              ? `Не сфотографировано мест: ${missingPhotos}. Без них завершить нельзя.`
              : "Фото уйдут в заказ 1С, будет создана приходная накладная (непроведённая)."
          }
          confirmLabel="Завершить"
          confirmBusy={finish.isPending}
          onConfirm={() => finish.mutate()}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === "delete" && (
        <Dialog
          title="Удалить приход?"
          body="Заведённые партии будут помечены на удаление в 1С. Напечатанные этикетки придётся снять с тары вручную."
          confirmLabel="Удалить"
          danger
          confirmBusy={dropDelivery.isPending}
          onConfirm={() => dropDelivery.mutate()}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-slate-500">{label}</span>
      {children}
    </label>
  );
}

export default ReceivingDeliveryPage;

/**
 * Прогресс по строке заказа: сколько принято и сколько осталось.
 *
 * «Принято» — в ЭТОМ приходе (данные портала, видны сразу после приёма).
 * «Осталось» — по ЗАКАЗУ: остаток из 1С за вычетом принятого здесь. Без полосы
 * кладовщик считал в уме, сверяя две цифры в разных углах экрана.
 */
function LineProgress({
  line,
  accepted,
}: {
  line: OrderLineView;
  accepted: number;
}): React.ReactElement {
  const ordered = Number(line.qty || 0);
  const base = line.qty_remaining ?? ordered;
  const left = Math.max(0, base - accepted);
  const done = Math.max(0, ordered - left);
  const pct = ordered > 0 ? Math.min(100, Math.round((done / ordered) * 100)) : 0;
  return (
    <div className="w-28 shrink-0 text-right text-xs">
      <div className="font-semibold">
        {fmtNum(done)} / {fmtNum(ordered)} {line.unit_name}
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-slate-200">
        <div
          className={"h-full " + (left === 0 ? "bg-emerald-500" : "bg-sky-500")}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className={"mt-0.5 " + (left === 0 ? "text-emerald-700" : "text-slate-500")}>
        {left === 0 ? "принято" : `осталось ${fmtNum(left)}`}
      </div>
    </div>
  );
}

/** Число без хвоста «.0»: на складе считают штуками, а не дробями. */
function fmtNum(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Math.round(value * 1000) / 1000);
}
